import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { Rect, Transformer } from 'react-konva';

import { clampCropRect } from '../../utils/clampCropRect';
import { ANCHOR_SCREEN_PX, getScreenSizedAnchorStyle } from '../../utils/screenSizedAnchor';
import { getAspectRatioValue } from '../../config';
import { useMediaStudio } from '../../context/MediaStudioContext';

const toLocalBox = (box, stage) => {
  const scaleX = stage.scaleX() || 1;
  const scaleY = stage.scaleY() || 1;
  return {
    x: (box.x - stage.x()) / scaleX,
    y: (box.y - stage.y()) / scaleY,
    width: box.width / scaleX,
    height: box.height / scaleY,
  };
};

const toAbsoluteBox = (box, stage, templateBox) => {
  const scaleX = stage.scaleX() || 1;
  const scaleY = stage.scaleY() || 1;
  return {
    ...templateBox,
    x: box.x * scaleX + stage.x(),
    y: box.y * scaleY + stage.y(),
    width: box.width * scaleX,
    height: box.height * scaleY,
  };
};

const readNodeRect = (node, stageWidth, stageHeight, aspectRatio) => {
  if (!node) {
    return null;
  }

  return clampCropRect(
    {
      x: node.x(),
      y: node.y(),
      width: Math.max(20, node.width() * Math.abs(node.scaleX())),
      height: Math.max(20, node.height() * Math.abs(node.scaleY())),
    },
    stageWidth,
    stageHeight,
    aspectRatio,
  );
};

const maskFrames = (rect, stageWidth, stageHeight) => {
  const x = Math.max(0, Math.min(rect.x, stageWidth));
  const y = Math.max(0, Math.min(rect.y, stageHeight));
  const width = Math.max(0, Math.min(rect.width, stageWidth - x));
  const height = Math.max(0, Math.min(rect.height, stageHeight - y));

  return [
    { x: 0, y: 0, width: stageWidth, height: Math.max(0, y) },
    { x: 0, y, width: Math.max(0, x), height },
    { x: x + width, y, width: Math.max(0, stageWidth - x - width), height },
    { x: 0, y: y + height, width: stageWidth, height: Math.max(0, stageHeight - y - height) },
  ];
};

function MediaStudioCropOverlay({ cropRect, cropRatio, stageWidth, stageHeight, zoom = 1, onChange }) {
  const { themeColors, layoutMode } = useMediaStudio();
  const anchorStyle = useMemo(
    () => getScreenSizedAnchorStyle(layoutMode.isCoarsePointer),
    [layoutMode.isCoarsePointer],
  );
  const shapeRef = useRef(null);
  const transformerRef = useRef(null);
  const maskRefs = useRef([]);
  const interactingRef = useRef(false);
  const aspectRatio = getAspectRatioValue(cropRatio);
  const safeZoom = zoom || 1;

  const paintMasks = (rect) => {
    maskFrames(rect, stageWidth, stageHeight).forEach((frame, index) => {
      maskRefs.current[index]?.setAttrs(frame);
    });
  };

  useLayoutEffect(() => {
    const node = shapeRef.current;
    if (!node || !cropRect || interactingRef.current) {
      return;
    }

    node.scaleX(1);
    node.scaleY(1);
    node.position({ x: cropRect.x, y: cropRect.y });
    node.size({ width: cropRect.width, height: cropRect.height });
    maskFrames(cropRect, stageWidth, stageHeight).forEach((frame, index) => {
      maskRefs.current[index]?.setAttrs(frame);
    });
    transformerRef.current?.nodes([node]);
    transformerRef.current?.forceUpdate();
    node.getLayer()?.batchDraw();
  }, [cropRect, cropRatio, stageWidth, stageHeight, zoom]);

  if (!cropRect) {
    return null;
  }

  const syncMasksFromNode = () => {
    const next = readNodeRect(shapeRef.current, stageWidth, stageHeight, aspectRatio);
    if (next) {
      paintMasks(next);
      shapeRef.current?.getLayer()?.batchDraw();
    }
  };

  const commitFromNode = () => {
    const node = shapeRef.current;
    if (!node) {
      interactingRef.current = false;
      return;
    }

    const scaleX = Math.abs(node.scaleX() || 1);
    const scaleY = Math.abs(node.scaleY() || 1);
    node.scaleX(1);
    node.scaleY(1);

    const nextRect = clampCropRect(
      {
        x: node.x(),
        y: node.y(),
        width: Math.max(20, node.width() * scaleX),
        height: Math.max(20, node.height() * scaleY),
      },
      stageWidth,
      stageHeight,
      aspectRatio,
    );

    node.position({ x: nextRect.x, y: nextRect.y });
    node.size({ width: nextRect.width, height: nextRect.height });
    paintMasks(nextRect);
    interactingRef.current = false;
    onChange(nextRect);
  };

  return (
    <>
      {[0, 1, 2, 3].map((index) => (
        <Rect
          key={index}
          ref={(node) => {
            maskRefs.current[index] = node;
          }}
          fill='rgba(0,0,0,0.55)'
          listening={false}
        />
      ))}
      <Rect
        ref={shapeRef}
        x={cropRect.x}
        y={cropRect.y}
        width={cropRect.width}
        height={cropRect.height}
        fill='rgba(0,0,0,0.01)'
        stroke={themeColors.accent}
        strokeWidth={1.5 / safeZoom}
        dash={[6 / safeZoom, 4 / safeZoom]}
        draggable
        onDragStart={() => {
          interactingRef.current = true;
        }}
        onDragMove={syncMasksFromNode}
        onDragEnd={commitFromNode}
        onTransformStart={() => {
          interactingRef.current = true;
        }}
        onTransform={syncMasksFromNode}
        onTransformEnd={commitFromNode}
        dragBoundFunc={(absPos) => {
          const node = shapeRef.current;
          const stage = node?.getStage();
          if (!node || !stage) {
            return absPos;
          }

          const scale = stage.scaleX() || 1;
          const nodeWidth = Math.max(20, node.width() * Math.abs(node.scaleX() || 1));
          const nodeHeight = Math.max(20, node.height() * Math.abs(node.scaleY() || 1));
          const localX = (absPos.x - stage.x()) / scale;
          const localY = (absPos.y - stage.y()) / scale;

          return {
            x: stage.x() + Math.max(0, Math.min(localX, stageWidth - nodeWidth)) * scale,
            y: stage.y() + Math.max(0, Math.min(localY, stageHeight - nodeHeight)) * scale,
          };
        }}
      />
      <Transformer
        ref={transformerRef}
        rotateEnabled={false}
        keepRatio={Boolean(aspectRatio)}
        ignoreStroke
        borderEnabled={false}
        anchorSize={ANCHOR_SCREEN_PX}
        anchorCornerRadius={3}
        anchorStrokeWidth={1.5}
        anchorStyleFunc={anchorStyle}
        anchorStroke={themeColors.accent}
        anchorFill={themeColors.accentContrast}
        enabledAnchors={
          aspectRatio
            ? ['top-left', 'top-right', 'bottom-left', 'bottom-right']
            : [
                'top-left',
                'top-center',
                'top-right',
                'middle-right',
                'middle-left',
                'bottom-left',
                'bottom-center',
                'bottom-right',
              ]
        }
        boundBoxFunc={(oldBox, newBox) => {
          const node = shapeRef.current;
          const stage = node?.getStage();
          if (!stage) {
            return oldBox;
          }

          const local = toLocalBox(newBox, stage);
          if (local.width < 20 || local.height < 20) {
            return oldBox;
          }

          const clamped = clampCropRect(local, stageWidth, stageHeight, aspectRatio);
          if (clamped.width < 20 || clamped.height < 20) {
            return oldBox;
          }

          return toAbsoluteBox(clamped, stage, newBox);
        }}
      />
    </>
  );
}

export default MediaStudioCropOverlay;
